import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Stack, Chip, Alert, Button, TextField, IconButton, Tooltip,
  CircularProgress, LinearProgress, InputAdornment, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';
import FolderOpenRounded from '@mui/icons-material/FolderOpenRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import RuleRounded from '@mui/icons-material/RuleRounded';

import {
  getArtworkSpecCheck, ArtworkSpecCheckJob, ArtworkSpecStatus, ArtworkSpecCheckParams,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';
import { proofScopeUrl, fmtDate, apiErr } from './artworkHealthShared';

/**
 * What each row status MEANS. The backend (helpers.spec_check) is the single
 * source of the verdict — this file only says it in English and colours it.
 */
const STATUSES: Record<ArtworkSpecStatus, { label: string; blurb: string; colour: string }> = {
  mismatch: {
    label: 'Mismatch',
    blurb: 'The artwork we resolve for this job contradicts Prism\'s ink spec — an ECG job '
      + 'with a Pantone or named spot, a spec PMS that is not in the file, process colours '
      + 'that differ. Usually the wrong file version resolved; check the path first.',
    colour: TOKENS.bad,
  },
  unverified: {
    label: 'Can\'t verify',
    blurb: 'Nothing contradicts, but a free-text spot in the spec ("BULLA BUFF") could only '
      + 'be paired with an artwork separation by elimination, not by name. A human glance '
      + 'settles it.',
    colour: TOKENS.warn,
  },
  no_artwork: {
    label: 'No artwork',
    blurb: 'No cached separations for this job, so nothing to compare. The reason column says '
      + 'whether Cloudflow has no file at any path we try, or we simply have not read it yet.',
    colour: TOKENS.ink3,
  },
  no_spec: {
    label: 'No spec',
    blurb: 'Prism has no ink spec on the job. Nothing to compare against — fix it in Prism.',
    colour: TOKENS.ink3,
  },
  ok: {
    label: 'Match',
    blurb: 'Every colour the spec calls for is in the artwork and nothing extra is.',
    colour: TOKENS.good,
  },
};
const STATUS_ORDER: ArtworkSpecStatus[] = ['mismatch', 'unverified', 'no_artwork', 'no_spec', 'ok'];

const REASON_LABEL: Record<string, string> = {
  not_found: 'no file at any known path',
  not_warmed: 'not read yet — the nightly warm has not reached it',
  no_path: 'blank customer or item code',
  timeout: 'wave timed out reading it',
  transport: 'wave unreachable',
  cloudflow: 'Cloudflow error',
  config: 'Cloudflow not configured',
  error: 'read error',
};

const LETTER_NAME: Record<string, string> = {
  b: 'Black', c: 'Cyan', m: 'Magenta', y: 'Yellow', o: 'Orange', g: 'Green', v: 'Violet',
};
const LETTER_HEX: Record<string, string> = {
  b: '#1a1a1a', c: '#00a0e3', m: '#e5007d', y: '#ffe400', o: '#ff7a00', g: '#00a651', v: '#6f2c91',
};

function InkChip({ label, title, colour, outlined }: {
  label: string; title?: string; colour: string; outlined?: boolean;
}) {
  const chip = (
    <Chip size="small" label={label}
      sx={{
        height: 18, fontSize: 10.5, fontWeight: 700, mr: 0.4, mb: 0.4,
        color: outlined ? colour : '#fff',
        bgcolor: outlined ? 'transparent' : colour,
        border: outlined ? `1px solid ${colour}88` : 'none',
        '& .MuiChip-label': { px: 0.7 },
      }} />
  );
  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip;
}

/** The artwork side, as chips: process letters solid, ECG extras solid, the
 *  rest outlined in the colour of what they mean for the check. */
function ArtworkChips({ a }: { a: ArtworkSpecCheckJob['artwork'] }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
      {[...a.process, ...a.ecg_extras].map((l) => (
        <InkChip key={l} label={l.toUpperCase()} title={LETTER_NAME[l]}
          colour={LETTER_HEX[l] ?? TOKENS.ink} />
      ))}
      {a.pantone.map((p) => (
        <InkChip key={p.name} label={p.name.replace(/^PANTONE\s+/i, 'PMS ')} title={p.name}
          colour={TOKENS.warn} outlined />
      ))}
      {a.spots.map((s) => <InkChip key={s} label={s} colour={TOKENS.bad} outlined />)}
      {a.special.map((s) => <InkChip key={s} label={s} colour={TOKENS.ink3} outlined />)}
      {a.coatings.map((s) => (
        <InkChip key={s} label={s} title="Coating — not compared" colour={TOKENS.ink3} outlined />
      ))}
    </Box>
  );
}

/**
 * ArtworkSpecCheckTab — every open job whose Prism ink spec and Cloudflow
 * artwork disagree, worst-first.
 *
 * Same verdict the ink sticker prints in red when a job bag is scanned
 * (`ink_spec_mismatch`), applied to the whole order book before anyone
 * scans — and widened past the sticker's one rule (ECG with a Pantone) to
 * the other things the two strings can contradict each other on. Reads the
 * separation cache only; a job we have not read yet is listed as such, not
 * pulled from Cloudflow.
 */
export default function ArtworkSpecCheckTab({ onFix }: {
  onFix: (r: ArtworkSpecCheckJob) => void;
}) {
  const [status, setStatus] = useState<string>('mismatch');
  const [kind, setKind] = useState<string>('');
  const [search, setSearch] = useState('');
  const [openOnly, setOpenOnly] = useState(true);

  const params: ArtworkSpecCheckParams = {
    scope: openOnly ? 'open' : 'recent',
    status: status || undefined,
    kind: kind || undefined,
    search: search.trim() || undefined,
  };
  const q = useQuery({
    queryKey: productionKeys.artworkSpecCheck(params),
    queryFn: () => getArtworkSpecCheck(params),
    staleTime: 60 * 1000,
  });
  const data = q.data;
  const byKind = useMemo(() => data?.summary.by_kind ?? [], [data]);
  const active = status ? STATUSES[status as ArtworkSpecStatus] : null;

  return (
    <>
      <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3, mb: 1.25 }}>
        Prism's ink spec on each open job, checked against the separations in the artwork we
        resolve for it — the same test the ink sticker runs when a job bag is scanned, run
        here before anyone prints. A mismatch is nearly always the wrong file resolving:
        fix the path and the row re-checks straight away.
      </Typography>

      {/* ---- Status chips ------------------------------------------------ */}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
        <Chip size="small" label={`All · ${data?.summary.total ?? 0}`}
          onClick={() => setStatus('')}
          variant={status ? 'outlined' : 'filled'}
          sx={{ fontSize: 11.5, fontWeight: 700, ...(status ? {} : { color: '#fff', bgcolor: TOKENS.ink }) }} />
        {STATUS_ORDER.map((st) => {
          const meta = STATUSES[st];
          const n = data?.summary.by_status[st] ?? 0;
          const on = status === st;
          return (
            <Tooltip key={st} title={meta.blurb}>
              <Chip size="small" label={`${meta.label} · ${n}`}
                onClick={() => setStatus(on ? '' : st)}
                variant={on ? 'filled' : 'outlined'}
                sx={{
                  fontSize: 11.5, fontWeight: 700,
                  color: on ? '#fff' : meta.colour,
                  bgcolor: on ? meta.colour : 'transparent',
                  borderColor: `${meta.colour}66`,
                }} />
            </Tooltip>
          );
        })}
      </Stack>

      {/* ---- Kind chips: which rule tripped ------------------------------- */}
      {byKind.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          {byKind.map((k) => {
            const on = kind === k.kind;
            return (
              <Chip key={k.kind} size="small" label={`${k.label} · ${k.jobs}`}
                onClick={() => setKind(on ? '' : k.kind)}
                variant={on ? 'filled' : 'outlined'}
                sx={{
                  fontSize: 11, fontWeight: 600,
                  color: on ? '#fff' : TOKENS.ink2,
                  bgcolor: on ? TOKENS.ink2 : 'transparent',
                }} />
            );
          })}
        </Stack>
      )}

      {active && (
        <Alert severity="info" sx={{ mb: 1.25, py: 0.25, fontSize: 12.5 }}>
          <strong>{active.label}</strong> — {active.blurb}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.25 }}>
        <TextField
          size="small" sx={{ minWidth: 300 }}
          placeholder="Filter by job, client, item, description or spec…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchRounded sx={{ fontSize: 16 }} /></InputAdornment>
            ),
            sx: { fontSize: 12.5 },
          }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />}
          label={
            <Tooltip title="Off widens the list to jobs due in the last 60 days, so a job that just ran can be checked too.">
              <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>
                Open jobs only{data ? ` (${data.summary.open_jobs})` : ''}
              </Typography>
            </Tooltip>
          } />
      </Stack>

      {q.isFetching && <LinearProgress sx={{ height: 2, mb: 1 }} />}
      {q.isError && (
        <Alert severity="error" sx={{ fontSize: 12.5 }}>Couldn't load: {apiErr(q.error)}</Alert>
      )}
      {q.isLoading && (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>
      )}

      {data && !data.jobs.length && !q.isLoading && (
        <Stack alignItems="center" sx={{ py: 8 }} spacing={1}>
          <RuleRounded sx={{ fontSize: 40, color: TOKENS.good, opacity: 0.5 }} />
          <Typography sx={{ fontSize: 14, color: TOKENS.ink2, fontWeight: 600 }}>
            {status === 'mismatch' && !kind && !search.trim()
              ? 'Every open job with artwork matches its Prism spec.'
              : 'Nothing matches those filters.'}
          </Typography>
        </Stack>
      )}

      {!!data?.jobs.length && (
        <>
          <SpecTable rows={data.jobs} onFix={onFix} />
          {data.matched > data.returned && (
            <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, mt: 1 }}>
              Showing the first {data.returned} of {data.matched} — narrow the filters to reach
              the rest.
            </Typography>
          )}
        </>
      )}
    </>
  );
}

const HEAD = { fontWeight: 700, fontSize: 11, color: TOKENS.ink3, py: 0.5 } as const;
const CELL = { fontSize: 12, py: 0.6, verticalAlign: 'top' } as const;

function SpecTable({ rows, onFix }: {
  rows: ArtworkSpecCheckJob[];
  onFix: (r: ArtworkSpecCheckJob) => void;
}) {
  return (
    <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)', px: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD, width: 70 }}>Job</TableCell>
            <TableCell sx={{ ...HEAD, width: 64 }}>Client</TableCell>
            <TableCell sx={{ ...HEAD, width: 150 }}>Item</TableCell>
            <TableCell sx={{ ...HEAD, width: 84 }}>Due</TableCell>
            <TableCell sx={{ ...HEAD, width: 190 }}>Prism spec</TableCell>
            <TableCell sx={{ ...HEAD, minWidth: 220 }}>Artwork separations</TableCell>
            <TableCell sx={{ ...HEAD, minWidth: 260 }}>Result</TableCell>
            <TableCell sx={{ ...HEAD, width: 92 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const meta = STATUSES[r.status];
            return (
              <TableRow key={r.job_number} hover>
                <TableCell sx={{ ...CELL, fontFamily: TOKENS.monoFont, fontWeight: 700 }}>
                  {r.job_number}
                  {r.scheduled_press && (
                    <Tooltip title={`Scheduled on ${r.scheduled_press}`}>
                      <Chip size="small" label={r.scheduled_press}
                        sx={{
                          display: 'block', width: 'fit-content', mt: 0.4, height: 16, fontSize: 9.5,
                          fontWeight: 700, color: TOKENS.accent, bgcolor: `${TOKENS.accent}1a`,
                        }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ ...CELL, fontWeight: 700 }}>{r.customer_code}</TableCell>
                <TableCell sx={CELL}>
                  <Box sx={{ fontFamily: TOKENS.monoFont, wordBreak: 'break-all' }}>
                    {r.customer_ref}
                    {r.artwork_path_overridden && (
                      <Tooltip title={`Path was corrected by hand: ${r.artwork_path}`}>
                        <span style={{ color: TOKENS.accent, fontWeight: 700, marginLeft: 4 }}>✎</span>
                      </Tooltip>
                    )}
                  </Box>
                  {r.description && (
                    <Box sx={{ color: TOKENS.ink3, fontSize: 11, lineHeight: 1.25, mt: 0.25 }}>
                      {r.description}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ ...CELL, color: TOKENS.ink2 }}>{fmtDate(r.due_date)}</TableCell>
                <TableCell sx={{ ...CELL, fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink2, wordBreak: 'break-word' }}>
                  {r.print_spec ?? <span style={{ color: TOKENS.bad, fontWeight: 700 }}>blank</span>}
                </TableCell>
                <TableCell sx={CELL}>
                  {r.status === 'no_artwork'
                    ? (
                      <span style={{ color: TOKENS.ink3 }}>
                        — {REASON_LABEL[r.artwork_reason ?? ''] ?? r.artwork_reason ?? 'not read'}
                      </span>
                    )
                    : <ArtworkChips a={r.artwork} />}
                </TableCell>
                <TableCell sx={CELL}>
                  <Chip size="small" label={meta.label}
                    sx={{
                      height: 19, fontSize: 10.5, fontWeight: 700, mb: 0.4,
                      color: meta.colour, bgcolor: `${meta.colour}1a`,
                    }} />
                  {r.issues.map((i) => (
                    <Box key={i.kind + i.detail} sx={{ fontSize: 11.5, color: TOKENS.ink, lineHeight: 1.3, mt: 0.3 }}>
                      {i.detail}
                    </Box>
                  ))}
                  {r.notes.map((n) => (
                    <Box key={n} sx={{ fontSize: 11, color: TOKENS.ink3, fontStyle: 'italic', lineHeight: 1.3, mt: 0.3 }}>
                      {n}
                    </Box>
                  ))}
                </TableCell>
                <TableCell sx={{ ...CELL, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Tooltip title="Browse Cloudflow, pick the right PDF, and re-check now">
                    <Button size="small" startIcon={<FolderOpenRounded sx={{ fontSize: 15 }} />}
                      onClick={() => onFix(r)}
                      sx={{ fontSize: 11, textTransform: 'none', minWidth: 0, px: 0.75, color: TOKENS.accent }}>
                      Fix path
                    </Button>
                  </Tooltip>
                  {r.artwork_path && r.status !== 'no_artwork' && (
                    <Tooltip title="Open in ProofScope">
                      <IconButton size="small" component="a" target="_blank" rel="noreferrer"
                        href={proofScopeUrl(r.artwork_path)}>
                        <OpenInNewRounded sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
